import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react'
import { Offcanvas, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { UpdateInventoryCategoryFormData, UpdateInventoryCategorySchema } from '../../../services/inventory-category/schema/UpdateInventoryCategorySchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateInventoryCategory } from '../../../services/inventory-category/hooks/useUpdateInventoryCategory';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import useShowInventoryCategory from '../../../services/inventory-category/hooks/useShowInventoryCategory';

interface EditInventoryCategoryDialogProps {
    show: boolean;
    onClose: () => void;
    inventoryCategoryId: string;
}

const EditInventoryCategoryDialog: React.FC<EditInventoryCategoryDialogProps> = ({ show, onClose, inventoryCategoryId }) => {
    const { register, handleSubmit, formState: { errors }, reset, setError, setValue } = useForm<UpdateInventoryCategoryFormData>({
        resolver: zodResolver(UpdateInventoryCategorySchema),
        defaultValues: {
            name: '',
        }
    });

    const { data: inventoryCategoryData, isSuccess: inventoryCategoryLoaded, isLoading: inventoryCategoryLoading } = useShowInventoryCategory({ id: inventoryCategoryId });

    useEffect(() => {
        if (inventoryCategoryLoaded && inventoryCategoryData) {
            setValue('name', inventoryCategoryData.data.name);
        }
    }, [inventoryCategoryLoaded, inventoryCategoryData, setValue]);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const { mutate, isPending } = useUpdateInventoryCategory();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateInventoryCategoryFormData) => {
        if (isPending) return;

        toast.loading('Updating inventory category...', { id: 'update-inventory-category' });
        mutate({ inventoryCategoryId, formData: data }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['inventory-categories'].includes(key as string))
                });

                toast.success('Inventory category updated successfully', { id: 'update-inventory-category' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateInventoryCategoryFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update inventory category', { id: 'update-inventory-category' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to update inventory category', { id: 'update-inventory-category' });
                }
            }
        });
    }, [mutate, inventoryCategoryId, isPending, handleClose, queryClient, setError]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Edit Inventory Category</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {inventoryCategoryLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                        <div className="action-sheet-content flex-grow-1">
                            {/* Name */}
                            <div className="row mb-3 align-items-center">
                                <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Name: </label>
                                <div className="col-sm-8">
                                    <input
                                        type="text"
                                        className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                        id="name"
                                        placeholder="Name"
                                        {...register('name')}
                                        style={{ fontSize: '14px', height: '35px' }}
                                    />
                                    {errors?.name && <div className="invalid-feedback">{errors.name.message}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="form-group mt-auto d-grid">
                            <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                                {isPending ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default EditInventoryCategoryDialog
